import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Input from "@mui/joy/Input";
import Textarea from "@mui/joy/Textarea";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import IconButton from "@mui/joy/IconButton";
import Sheet from "@mui/joy/Sheet";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/joy/styles";
import {
    AD_CAMPAIGN_CHANNELS,
    AdCampaign,
    AdCampaignStatus,
    AdCampaignStatusLabels,
    getAllowedAdCampaignStatuses,
} from "../../models/adCampaign";
import { adCampaignCreateApi, adCampaignGetApi, adCampaignUpdateApi } from "./adCampaignsApi";
import { NotificationService } from "../../common/notification";
import { kopecksToRublesNumber, rublesToKopecksNumber } from "../../common/money";
import { FieldHint } from "./FieldHint";

function toLocalInputValue(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local: string): string {
    const d = new Date(local);
    return d.toISOString();
}

/** Поле ввода числа → число, null для пустого, NaN для невалидного (запятая допустима). */
function parseOptionalNumber(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed === "") return null;
    return Number(trimmed.replace(",", "."));
}

function toInputValue(value?: number | null): string {
    return value != null ? String(value) : "";
}

/** Лимит активаций хранится в int на сервере. */
const MAX_ACTIVATIONS_LIMIT = 2147483647;

const MAX_PROMO_CODE_LENGTH = 16;

/** Снимок полей бонусов: отправляем их на сервер, только если админ их менял. */
function promoBonusesKey(values: string[]): string {
    return values.map((v) => v.trim()).join("|");
}

export function AdCampaignEditor() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation() as { state?: { campaign?: AdCampaign } };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isEdit = Boolean(id);

    const [name, setName] = React.useState("");
    const [channel, setChannel] = React.useState<string>("Vk");
    const [landingUrl, setLandingUrl] = React.useState("https://");
    const [startAt, setStartAt] = React.useState(() => toLocalInputValue(new Date().toISOString()));
    const [endAt, setEndAt] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [notes, setNotes] = React.useState("");
    const [promoCode, setPromoCode] = React.useState("");
    const [spendRub, setSpendRub] = React.useState("");
    const [saleCommissionPercent, setSaleCommissionPercent] = React.useState("");
    const [purchaseDiscountPercent, setPurchaseDiscountPercent] = React.useState("");
    const [depositBonusPercent, setDepositBonusPercent] = React.useState("");
    const [maxActivations, setMaxActivations] = React.useState("");
    /** Бонусы, загруженные с сервера: без изменений не перезаписываем (могли поменять в другой вкладке). */
    const [loadedBonusesKey, setLoadedBonusesKey] = React.useState(promoBonusesKey(["", "", "", ""]));
    const [status, setStatus] = React.useState<AdCampaignStatus>(AdCampaignStatus.Draft);
    const [trackingUrl, setTrackingUrl] = React.useState("");
    const [code, setCode] = React.useState("");
    const [loading, setLoading] = React.useState(isEdit);
    const [saving, setSaving] = React.useState(false);

    /** Текущий статус в БД (для списка допустимых переходов); при создании — Draft. */
    const [persistedStatus, setPersistedStatus] = React.useState<AdCampaignStatus>(AdCampaignStatus.Draft);

    const applyCampaign = (c: AdCampaign) => {
        setName(c.name);
        setChannel(c.channel);
        setLandingUrl(c.landingUrl);
        setStartAt(toLocalInputValue(c.startAt));
        setEndAt(toLocalInputValue(c.endAt));
        setDescription(c.description || "");
        setNotes(c.notes || "");
        setPromoCode(c.promoCode || "");
        setSpendRub(
            c.spendKopecks != null ? String(kopecksToRublesNumber(c.spendKopecks)) : ""
        );
        setSaleCommissionPercent(toInputValue(c.saleCommissionPercent));
        setPurchaseDiscountPercent(toInputValue(c.purchaseDiscountPercent));
        setDepositBonusPercent(toInputValue(c.depositBonusPercent));
        setMaxActivations(toInputValue(c.maxActivations));
        setLoadedBonusesKey(promoBonusesKey([
            toInputValue(c.saleCommissionPercent),
            toInputValue(c.purchaseDiscountPercent),
            toInputValue(c.depositBonusPercent),
            toInputValue(c.maxActivations),
        ]));
        setStatus(c.status);
        setPersistedStatus(c.status);
        setTrackingUrl(c.trackingUrl);
        setCode(c.code);
    };

    React.useEffect(() => {
        if (!isEdit || !id) {
            setLoading(false);
            return;
        }
        const fromState = location.state?.campaign;
        if (fromState && fromState.id === Number(id)) {
            applyCampaign(fromState);
            setLoading(false);
            return;
        }
        (async () => {
            const res = await adCampaignGetApi(Number(id));
            if (res.result && res.value) {
                applyCampaign(res.value);
            } else {
                NotificationService.alertError("Кампания не найдена");
                navigate("/app/ad-campaigns");
            }
            setLoading(false);
        })().catch(console.error);
    }, [id, isEdit]);

    const save = async () => {
        if (saving) return;
        if (!name.trim() || !channel.trim() || !landingUrl.trim() || !startAt) {
            NotificationService.alertError("Заполните название, канал, landing и дату начала");
            return;
        }
        if (promoCode.trim().length > MAX_PROMO_CODE_LENGTH) {
            NotificationService.alertError(`Промокод — не длиннее ${MAX_PROMO_CODE_LENGTH} символов`);
            return;
        }

        // Диапазоны процентов и лимита проверяет сервер — здесь только формат числа.
        const promoBonuses = {
            saleCommissionPercent: parseOptionalNumber(saleCommissionPercent),
            purchaseDiscountPercent: parseOptionalNumber(purchaseDiscountPercent),
            depositBonusPercent: parseOptionalNumber(depositBonusPercent),
            maxActivations: parseOptionalNumber(maxActivations),
        };
        if (Object.values(promoBonuses).some((v) => v != null && !Number.isFinite(v))) {
            NotificationService.alertError("Бонусы промокода и лимит — числа");
            return;
        }
        if (
            promoBonuses.maxActivations != null &&
            (!Number.isInteger(promoBonuses.maxActivations) ||
                promoBonuses.maxActivations < 1 ||
                promoBonuses.maxActivations > MAX_ACTIVATIONS_LIMIT)
        ) {
            NotificationService.alertError("Лимит активаций — целое число от 1");
            return;
        }
        const bonusesChanged =
            promoBonusesKey([saleCommissionPercent, purchaseDiscountPercent, depositBonusPercent, maxActivations]) !==
            loadedBonusesKey;

        setSaving(true);
        try {
            const spend =
                spendRub.trim() === "" ? null : rublesToKopecksNumber(spendRub);
            const endIso = endAt ? fromLocalInputValue(endAt) : null;

            if (isEdit && id) {
                const ok = await adCampaignUpdateApi({
                    id: Number(id),
                    name: name.trim(),
                    channel: channel.trim(),
                    landingUrl: landingUrl.trim(),
                    startAt: fromLocalInputValue(startAt),
                    endAt: endIso,
                    clearEndAt: !endAt,
                    description: description.trim() || null,
                    notes: notes.trim() || null,
                    promoCode: promoCode.trim() || null,
                    clearPromoCode: !promoCode.trim(),
                    spendKopecks: spend,
                    clearSpend: spend == null,
                    status,
                    ...(bonusesChanged ? { updatePromoBonuses: true, ...promoBonuses } : {}),
                });
                if (!ok.result) {
                    NotificationService.alertError(ok.error || "Не удалось сохранить");
                    return;
                }
                NotificationService.alertSuccess("Сохранено");
                const refreshed = await adCampaignGetApi(Number(id));
                if (refreshed.result && refreshed.value) {
                    applyCampaign(refreshed.value);
                }
            } else {
                const created = await adCampaignCreateApi({
                    name: name.trim(),
                    channel: channel.trim(),
                    landingUrl: landingUrl.trim(),
                    startAt: fromLocalInputValue(startAt),
                    endAt: endIso,
                    description: description.trim() || null,
                    notes: notes.trim() || null,
                    promoCode: promoCode.trim() || null,
                    spendKopecks: spend,
                    status,
                    ...promoBonuses,
                });
                if (!created.result || !created.value) {
                    NotificationService.alertError(created.error || "Не удалось создать кампанию");
                    return;
                }
                NotificationService.alertSuccess("Кампания создана");
                navigate(`/app/ad-campaigns/${created.value.id}/edit`, {
                    replace: true,
                    state: { campaign: created.value },
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const copyUrl = async () => {
        if (!trackingUrl) return;
        try {
            await navigator.clipboard.writeText(trackingUrl);
            NotificationService.alertSuccess("Ссылка скопирована");
        } catch {
            NotificationService.alertError("Не удалось скопировать");
        }
    };

    const allowedStatuses = getAllowedAdCampaignStatuses(
        isEdit ? persistedStatus : AdCampaignStatus.Draft,
        { isCreate: !isEdit }
    );
    const statusLocked = isEdit && persistedStatus === AdCampaignStatus.Finished;

    if (loading) {
        return (
            <Box
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: {
                        xs: "calc(12px + var(--Header-height))",
                        md: 3,
                    },
                }}
            >
                <Typography>Загрузка…</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", minHeight: "100dvh" }}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: {
                        xs: "calc(12px + var(--Header-height))",
                        sm: "calc(12px + var(--Header-height))",
                        md: 3,
                    },
                    pb: { xs: 10, md: 3 },
                    flex: 1,
                    width: "100%",
                    maxWidth: { xs: "100%", md: 720 },
                    minWidth: 0,
                    overflow: "auto",
                }}
            >
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                    <IconButton variant="plain" onClick={() => navigate("/app/ad-campaigns")}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        level="h2"
                        component="h1"
                        sx={{
                            fontSize: { xs: "xl", sm: "xl2", md: "xl3" },
                            lineHeight: 1.25,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {isEdit ? (isMobile ? "Кампания" : "Редактирование кампании") : "Новая кампания"}
                    </Typography>
                </Stack>

                <Stack gap={1.5}>
                    <FormControl required>
                        <FormLabel>Название</FormLabel>
                        <Input value={name} onChange={(e) => setName(e.target.value)} />
                    </FormControl>

                    <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                        <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Канал</FormLabel>
                            <Select value={channel} onChange={(_, v) => v && setChannel(v)}>
                                {AD_CAMPAIGN_CHANNELS.map((ch) => (
                                    <Option key={ch} value={ch}>
                                        {ch}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Статус</FormLabel>
                            <Select
                                value={status}
                                disabled={statusLocked}
                                onChange={(_, v) => v != null && setStatus(v)}
                            >
                                {allowedStatuses.map((v) => (
                                    <Option key={v} value={v}>
                                        {AdCampaignStatusLabels[v]}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <FormControl required>
                        <FormLabel>Landing URL</FormLabel>
                        <Input
                            value={landingUrl}
                            onChange={(e) => setLandingUrl(e.target.value)}
                            placeholder="https://example.com/"
                            sx={{ "& input": { fontSize: { xs: "sm", sm: "md" } } }}
                        />
                    </FormControl>

                    <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                        <FormControl required sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Начало</FormLabel>
                            <Input
                                type="datetime-local"
                                value={startAt}
                                onChange={(e) => setStartAt(e.target.value)}
                                sx={{ "& input": { minWidth: 0 } }}
                            />
                        </FormControl>
                        <FormControl sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Окончание (опционально)</FormLabel>
                            <Input
                                type="datetime-local"
                                value={endAt}
                                onChange={(e) => setEndAt(e.target.value)}
                                sx={{ "& input": { minWidth: 0 } }}
                            />
                        </FormControl>
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                        <FormControl sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>Промокод (опционально)</FormLabel>
                            <Input
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value)}
                                placeholder="SPRING26"
                                slotProps={{ input: { maxLength: MAX_PROMO_CODE_LENGTH } }}
                            />
                            <FormHelperText>
                                {promoCode.trim().length}/{MAX_PROMO_CODE_LENGTH}
                            </FormHelperText>
                        </FormControl>
                        <FormControl sx={{ flex: 1, minWidth: 0 }}>
                            <FormLabel>
                                Spend, ₽ (опционально)
                                <FieldHint
                                    title="Spend — затраты на кампанию"
                                    lines={[
                                        "Сколько потрачено (или запланировано) на рекламу целиком: оплата блогеру, бюджет на таргет и т.п. Одна сумма на всю кампанию, не в день.",
                                        "На пользователей и промокод не влияет — нужен только для отчётов юнит-экономики: CAC, ROAS, ROMI, окупаемость (payback), стоимость активации и сделки, net contribution.",
                                        "Пустое поле — эти метрики показываются как «—». Если бюджет вырос, обновите сумму: отчёты пересчитаются.",
                                    ]}
                                    examples={[
                                        "Spend 50 000 ₽, пришло 200 пользователей → CAC = 250 ₽ на пользователя.",
                                        "Прибыль с когорты 75 000 ₽, затраты на промо 5 000 ₽ → ROAS = (75 000 − 5 000) / 50 000 = 1,4×.",
                                    ]}
                                />
                            </FormLabel>
                            <Input
                                value={spendRub}
                                onChange={(e) => setSpendRub(e.target.value)}
                                placeholder="0"
                                inputMode="decimal"
                            />
                        </FormControl>
                    </Stack>

                    <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: "sm" }}>
                        <Stack gap={1.5}>
                            <Box>
                                <Typography level="title-sm">Бонусы промокода</Typography>
                                <Typography level="body-xs" color="neutral">
                                    Только для новых пользователей; срабатывает один бонус — на событие, которое
                                    наступит первым. Пустое поле — бонуса нет. Условия фиксируются при активации:
                                    правка не меняет уже активированные промокоды.
                                </Typography>
                            </Box>
                            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                                <FormControl sx={{ flex: 1, minWidth: 0 }}>
                                    <FormLabel>
                                        Комиссия на продажу, %
                                        <FieldHint
                                            title="Комиссия на продажу"
                                            lines={[
                                                "На первой продаже пользователь платит указанный % от цены вместо обычной комиссии. Разницу продавец получает себе, покупатель платит столько же.",
                                                "Действует, только если она ниже обычной комиссии. Если не ниже — бонус не применяется, промокод остаётся активным.",
                                                "0 — продажа совсем без комиссии.",
                                            ]}
                                            examples={[
                                                "Обычная комиссия 5%, по промокоду 2%. Скин за 1 000 ₽: комиссия 20 ₽ вместо 50 ₽, продавец получит 980 ₽ вместо 950 ₽. Наши затраты — 30 ₽.",
                                                "По промокоду 0%: продавец получит все 1 000 ₽.",
                                                "По промокоду 7% при обычной 5%: бонус не сработает, комиссия останется 50 ₽.",
                                            ]}
                                        />
                                    </FormLabel>
                                    <Input
                                        value={saleCommissionPercent}
                                        onChange={(e) => setSaleCommissionPercent(e.target.value)}
                                        placeholder="например, 2"
                                        inputMode="decimal"
                                    />
                                    <FormHelperText>Вместо обычной, 0–100</FormHelperText>
                                </FormControl>
                                <FormControl sx={{ flex: 1, minWidth: 0 }}>
                                    <FormLabel>
                                        Скидка на покупку, %
                                        <FieldHint
                                            title="Скидка на покупку"
                                            lines={[
                                                "На первой покупке пользователь платит цену минус указанный % (скидка на каждый предмет заказа).",
                                                "Скидка идёт из нашей комиссии: продавец получает столько же, сколько без промокода. Если скидка больше комиссии, разницу доплачиваем мы.",
                                            ]}
                                            examples={[
                                                "Скидка 5%. Скин за 1 000 ₽: покупатель заплатит 950 ₽, продавец получит как обычно. Наши затраты — 50 ₽.",
                                                "Заказ из трёх скинов по 400 ₽ со скидкой 10%: покупатель заплатит 1 080 ₽ вместо 1 200 ₽.",
                                            ]}
                                        />
                                    </FormLabel>
                                    <Input
                                        value={purchaseDiscountPercent}
                                        onChange={(e) => setPurchaseDiscountPercent(e.target.value)}
                                        placeholder="например, 5"
                                        inputMode="decimal"
                                    />
                                    <FormHelperText>Из нашей комиссии</FormHelperText>
                                </FormControl>
                            </Stack>
                            <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                                <FormControl sx={{ flex: 1, minWidth: 0 }}>
                                    <FormLabel>
                                        Бонус к депозиту, %
                                        <FieldHint
                                            title="Бонус к депозиту"
                                            lines={[
                                                "При первом успешном пополнении на баланс дополнительно начисляется указанный % от суммы — отдельной операцией «промо-бонус».",
                                                "Комиссия за пополнение не меняется. Ручные пополнения из админки бонус не получают. При чарджбеке или возврате депозита бонус списывается пропорционально.",
                                            ]}
                                            examples={[
                                                "Бонус 3%. Пополнение на 1 000 ₽: на баланс придёт 1 000 ₽ + 30 ₽ бонуса. Наши затраты — 30 ₽.",
                                                "Бонус 10%, пополнение на 5 000 ₽: +500 ₽ к балансу.",
                                            ]}
                                        />
                                    </FormLabel>
                                    <Input
                                        value={depositBonusPercent}
                                        onChange={(e) => setDepositBonusPercent(e.target.value)}
                                        placeholder="например, 3"
                                        inputMode="decimal"
                                    />
                                    <FormHelperText>+M% от суммы депозита</FormHelperText>
                                </FormControl>
                                <FormControl sx={{ flex: 1, minWidth: 0 }}>
                                    <FormLabel>
                                        Лимит активаций
                                        <FieldHint
                                            title="Лимит активаций"
                                            lines={[
                                                "Сколько пользователей всего могут ввести промокод. Считаются активные (введены, бонус ещё не получен) и израсходованные.",
                                                "Не считаются промокоды, которые пользователь заменил другим, и истёкшие. Когда лимит исчерпан, при вводе пользователь получит ошибку.",
                                                "Пустое поле — без ограничения.",
                                            ]}
                                            examples={[
                                                "Лимит 100: промокод ввели 100 человек — 101-й получит отказ.",
                                                "Из 100 один заменил промокод на другой — освободилось место, его может занять ещё один пользователь.",
                                            ]}
                                        />
                                    </FormLabel>
                                    <Input
                                        value={maxActivations}
                                        onChange={(e) => setMaxActivations(e.target.value)}
                                        placeholder="без ограничения"
                                        inputMode="numeric"
                                    />
                                    <FormHelperText>Активные и израсходованные</FormHelperText>
                                </FormControl>
                            </Stack>
                        </Stack>
                    </Sheet>

                    <FormControl>
                        <FormLabel>Описание</FormLabel>
                        <Textarea minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Заметки</FormLabel>
                        <Textarea minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </FormControl>

                    {isEdit && (
                        <Sheet variant="soft" sx={{ p: 1.5, borderRadius: "sm" }}>
                            <Stack gap={1}>
                                <Typography level="title-sm" sx={{ wordBreak: "break-all" }}>
                                    Code: {code}
                                </Typography>
                                <FormControl>
                                    <FormLabel>Ссылка для рекламщиков (c + UTM)</FormLabel>
                                    <Input
                                        value={trackingUrl}
                                        readOnly
                                        sx={{
                                            "& input": {
                                                fontSize: "xs",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            },
                                        }}
                                        endDecorator={
                                            <IconButton onClick={copyUrl} aria-label="Копировать ссылку">
                                                <ContentCopyIcon />
                                            </IconButton>
                                        }
                                    />
                                </FormControl>
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    startDecorator={<ContentCopyIcon />}
                                    onClick={copyUrl}
                                    sx={{ display: { xs: "flex", sm: "none" }, alignSelf: "stretch" }}
                                >
                                    Копировать ссылку
                                </Button>
                            </Stack>
                        </Sheet>
                    )}

                    {/* Desktop save */}
                    <Button
                        startDecorator={<SaveIcon />}
                        loading={saving}
                        onClick={() => save().catch(console.error)}
                        sx={{
                            alignSelf: "flex-start",
                            display: { xs: "none", sm: "inline-flex" },
                        }}
                    >
                        Сохранить
                    </Button>
                </Stack>
            </Box>

            {/* Mobile sticky save bar */}
            <Box
                sx={{
                    display: { xs: "block", sm: "none" },
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: 1.5,
                    bgcolor: "background.surface",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    zIndex: 100,
                }}
            >
                <Button
                    fullWidth
                    size="lg"
                    startDecorator={<SaveIcon />}
                    loading={saving}
                    onClick={() => save().catch(console.error)}
                >
                    Сохранить
                </Button>
            </Box>
        </Box>
    );
}
